import { useRef, useEffect, useCallback } from 'react';

/**
 * Custom hook for managing AbortController lifecycle in React components.
 * Provides consistent abort controller management with automatic cleanup.
 */
export const useAbortController = () => {
  const controllerRef = useRef<AbortController | null>(null);

  /**
   * Creates a new AbortController, aborting any previous one.
   * Use this when starting a new request that should cancel previous requests.
   */
  const createController = useCallback((): AbortController => {
    controllerRef.current?.abort();
    controllerRef.current = new AbortController();
    return controllerRef.current;
  }, []);

  /**
   * Gets the current AbortController without creating a new one.
   * Returns null if no controller exists.
   */
  const getCurrentController = useCallback((): AbortController | null => {
    return controllerRef.current;
  }, []);

  /**
   * Manually aborts the current request.
   * Useful for canceling requests based on user actions.
   */
  const abort = useCallback((): void => {
    controllerRef.current?.abort();
    controllerRef.current = null;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      controllerRef.current?.abort();
    };
  }, []);

  return {
    createController,
    getCurrentController,
    abort
  };
};

/**
 * Simplified version for components that only need basic create/cleanup functionality
 */
export const useSimpleAbortController = (): (() => AbortController) => {
  const { createController } = useAbortController();
  return createController;
};